// Tasks as tracked work: statuses, fields, tags, the task list, edits, links, checklists, files, comments.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, patch, post, put, uploadToPresignedUrl } from "./client";
import { invalidateTask, taskKey, taskPath } from "./testing";
import type { AttachmentKind } from "./types/AttachmentKind";
import type { BulkChange } from "./types/BulkChange";
import type { BulkTaskResult } from "./types/BulkTaskResult";
import type { CreateViewRequest } from "./types/CreateViewRequest";
import type { SavedView } from "./types/SavedView";
import type { TestingFlow } from "./types/TestingFlow";
import type { UpdateViewRequest } from "./types/UpdateViewRequest";
import type { ChatMessage } from "./types/ChatMessage";
import type { ChatPage } from "./types/ChatPage";
import type { Checklist } from "./types/Checklist";
import type { CreateFieldRequest } from "./types/CreateFieldRequest";
import type { CreateLinkRequest } from "./types/CreateLinkRequest";
import type { CreateStatusRequest } from "./types/CreateStatusRequest";
import type { CreateTagRequest } from "./types/CreateTagRequest";
import type { CreateTaskRequest } from "./types/CreateTaskRequest";
import type { FieldDef } from "./types/FieldDef";
import type { FieldValue } from "./types/FieldValue";
import type { FileUpload } from "./types/FileUpload";
import type { FileView } from "./types/FileView";
import type { Tag } from "./types/Tag";
import type { TaskDetail } from "./types/TaskDetail";
import type { TaskLink } from "./types/TaskLink";
import type { TaskStatus } from "./types/TaskStatus";
import type { TaskSummary } from "./types/TaskSummary";
import type { UpdateChecklistItemRequest } from "./types/UpdateChecklistItemRequest";
import type { UpdateFieldRequest } from "./types/UpdateFieldRequest";
import type { UpdateStatusRequest } from "./types/UpdateStatusRequest";
import type { UpdateTagRequest } from "./types/UpdateTagRequest";
import type { UpdateTaskRequest } from "./types/UpdateTaskRequest";

const enc = encodeURIComponent;
const projectPath = (key: string) => `/projects/${enc(key)}`;

// ───────────── Statuses ─────────────

export function useStatuses(projectKey: string) {
  return useQuery({
    queryKey: ["statuses", projectKey],
    queryFn: () => get<TaskStatus[]>(`${projectPath(projectKey)}/statuses`),
    staleTime: 60_000,
    // Cross-project views have no project: statuses differ per project.
    enabled: !!projectKey,
  });
}

function useStatusMutation<V>(projectKey: string, fn: (v: V) => Promise<TaskStatus[]>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (list) => {
      qc.setQueryData(["statuses", projectKey], list);
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      // Deleting the testing status turns the testing flow off.
      void qc.invalidateQueries({ queryKey: ["testing-flow", projectKey] });
      void qc.invalidateQueries({ queryKey: ["project", projectKey] });
    },
  });
}

/** The project's testing status (the board's Test column) and where passed tasks go. */
export function useTestingFlow(projectKey: string) {
  return useQuery({
    queryKey: ["testing-flow", projectKey],
    queryFn: () => get<TestingFlow>(`${projectPath(projectKey)}/testing-flow`),
    enabled: !!projectKey,
  });
}

export function useUpdateTestingFlow(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: TestingFlow) => put<TestingFlow>(`${projectPath(projectKey)}/testing-flow`, b),
    onSuccess: (flow) => {
      qc.setQueryData(["testing-flow", projectKey], flow);
      void qc.invalidateQueries({ queryKey: ["project", projectKey] });
    },
  });
}

export const useCreateStatus = (projectKey: string) =>
  useStatusMutation(projectKey, (b: CreateStatusRequest) => post<TaskStatus[]>(`${projectPath(projectKey)}/statuses`, b));
export const useUpdateStatus = (projectKey: string) =>
  useStatusMutation(projectKey, ({ id, ...b }: UpdateStatusRequest & { id: string }) =>
    patch<TaskStatus[]>(`${projectPath(projectKey)}/statuses/${id}`, b),
  );
export const useDeleteStatus = (projectKey: string) =>
  useStatusMutation(projectKey, ({ id, moveTo }: { id: string; moveTo?: string }) =>
    del<TaskStatus[]>(`${projectPath(projectKey)}/statuses/${id}${moveTo ? `?move_to=${moveTo}` : ""}`),
  );
export const useReorderStatuses = (projectKey: string) =>
  useStatusMutation(projectKey, (ids: string[]) => put<TaskStatus[]>(`${projectPath(projectKey)}/statuses/order`, { ids }));

// ───────────── Custom fields ─────────────

export function useFields(projectKey: string) {
  return useQuery({
    queryKey: ["fields", projectKey],
    queryFn: () => get<FieldDef[]>(`${projectPath(projectKey)}/fields`),
    staleTime: 60_000,
    enabled: !!projectKey,
  });
}

function useFieldMutation<V>(projectKey: string, fn: (v: V) => Promise<FieldDef[]>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (list) => {
      qc.setQueryData(["fields", projectKey], list);
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export const useCreateField = (projectKey: string) =>
  useFieldMutation(projectKey, (b: CreateFieldRequest) => post<FieldDef[]>(`${projectPath(projectKey)}/fields`, b));
export const useUpdateField = (projectKey: string) =>
  useFieldMutation(projectKey, ({ id, ...b }: UpdateFieldRequest & { id: string }) =>
    patch<FieldDef[]>(`${projectPath(projectKey)}/fields/${id}`, b),
  );
export const useDeleteField = (projectKey: string) =>
  useFieldMutation(projectKey, (id: string) => del<FieldDef[]>(`${projectPath(projectKey)}/fields/${id}`));
export const useReorderFields = (projectKey: string) =>
  useFieldMutation(projectKey, (ids: string[]) => put<FieldDef[]>(`${projectPath(projectKey)}/fields/order`, { ids }));

// ───────────── Tags ─────────────

export function useTags() {
  return useQuery({ queryKey: ["tags"], queryFn: () => get<Tag[]>("/tags"), staleTime: 60_000 });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: CreateTagRequest) => post<Tag>("/tags", b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["tags"] }),
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...b }: UpdateTagRequest & { id: string }) => patch<Tag>(`/tags/${id}`, b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tags"] });
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["task"] });
    },
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/tags/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tags"] });
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["task"] });
    },
  });
}

// ───────────── Task lists ─────────────

/** Query string of `GET …/tasks` (see the server's `ListQuery`). Arrays repeat the key. */
export type TaskQuery = Partial<{
  project: string[];
  status: string[];
  category: string[];
  open: boolean;
  assignee: string[];
  tester: string;
  parent: string;
  top_level: boolean;
  tag: string[];
  /** `urgent`, `high`, `medium`, `low` or `none`. */
  priority: string[];
  overdue: boolean;
  due_from: string;
  due_to: string;
  has_due: boolean;
  q: string;
  field: string;
  field_op: string;
  field_value: string;
  /** A sprint id, `active` (each project's active sprint) or `backlog`. */
  sprint: string;
  /** Epic ids or `none`. */
  epic: string[];
  /** Release ids or `none`. */
  release: string[];
  sort: string;
  asc: boolean;
  limit: number;
}>;

export function toSearch(q: TaskQuery): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    // `false` is the default for plain flags, but `has_due` / `overdue` are yes / no / any.
    if (v === undefined || v === null || v === "" || (v === false && k !== "has_due" && k !== "overdue")) continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, String(x)));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** The query behind `useTaskList`, for pages that hold several lists at once (`useQueries`). */
export const taskListQuery = (projectKey: string | null, q: TaskQuery) => ({
  queryKey: ["tasks", projectKey ?? "*", q] as const,
  queryFn: () => get<TaskSummary[]>(`${projectKey ? projectPath(projectKey) : ""}/tasks${toSearch(q)}`),
});

/** Tasks of one project, or (no project key) across every project the caller can see. */
export function useTaskList(projectKey: string | null, q: TaskQuery, enabled = true) {
  return useQuery({
    ...taskListQuery(projectKey, q),
    placeholderData: (prev) => prev,
    enabled,
  });
}

export function useCreateTask(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CreateTaskRequest> & { title: string }) =>
      post<TaskDetail>(`${projectPath(projectKey)}/tasks`, {
        description: "",
        status_id: null,
        parent_id: null,
        due_at: null,
        priority: null,
        assignee_ids: [],
        tester_id: null,
        tag_ids: [],
        sprint_id: null,
        ...body,
      } satisfies CreateTaskRequest),
    onSuccess: (task) => {
      qc.setQueryData([...taskKey(projectKey, task.key), "detail"], task);
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["statuses", projectKey] });
      void qc.invalidateQueries({ queryKey: ["sprints", projectKey] });
      if (task.parent_id) void qc.invalidateQueries({ queryKey: ["task", projectKey] });
    },
  });
}

/** PATCH a task by key. Lists and the task itself refresh. */
export function usePatchTask(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: UpdateTaskRequest & { key: string }) => patch<TaskDetail>(taskPath(projectKey, key), body),
    onSuccess: (task, vars) => {
      qc.setQueryData([...taskKey(task.project_key, task.key), "detail"], task);
      invalidateTask(qc, projectKey, vars.key);
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["statuses"] });
      // Status, sprint and estimate edits move sprint totals and burndowns.
      void qc.invalidateQueries({ queryKey: ["sprints"] });
      // A release's lists and counts follow what's in it and where its tasks stand.
      void qc.invalidateQueries({ queryKey: ["releases"] });
    },
  });
}

export function useDeleteTask(projectKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => del(taskPath(projectKey, key)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["sprints", projectKey] });
      void qc.invalidateQueries({ queryKey: ["task", projectKey] });
      void qc.invalidateQueries({ queryKey: ["statuses", projectKey] });
    },
  });
}

function useTaskMutation<V, R>(projectKey: string, key: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      if (res && typeof res === "object" && "key" in res && "test_stage" in res) {
        qc.setQueryData([...taskKey(projectKey, key), "detail"], res);
      }
      invalidateTask(qc, projectKey, key);
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export const useAssign = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (userId: string) => post<TaskDetail>(`${taskPath(projectKey, key)}/assignees`, { user_id: userId }));
export const useUnassign = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (userId: string) => del<TaskDetail>(`${taskPath(projectKey, key)}/assignees/${userId}`));
export const useAttachTag = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (tagId: string) => put<TaskDetail>(`${taskPath(projectKey, key)}/tags/${tagId}`, {}));
export const useDetachTag = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (tagId: string) => del<TaskDetail>(`${taskPath(projectKey, key)}/tags/${tagId}`));

// ───────────── Links ─────────────

export function useLinks(projectKey: string, key: string) {
  return useQuery({
    queryKey: [...taskKey(projectKey, key), "links"],
    queryFn: () => get<TaskLink[]>(`${taskPath(projectKey, key)}/links`),
  });
}

export const useCreateLink = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (b: CreateLinkRequest) => post<TaskLink[]>(`${taskPath(projectKey, key)}/links`, b));
export const useDeleteLink = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (id: string) => del(`${taskPath(projectKey, key)}/links/${id}`));

// ───────────── Checklists ─────────────

export function useChecklists(projectKey: string, key: string) {
  return useQuery({
    queryKey: [...taskKey(projectKey, key), "checklists"],
    queryFn: () => get<Checklist[]>(`${taskPath(projectKey, key)}/checklists`),
  });
}

export function useChecklistMutation(projectKey: string, key: string) {
  const qc = useQueryClient();
  const base = `${taskPath(projectKey, key)}/checklists`;
  return useMutation({
    mutationFn: (
      a:
        | { op: "create"; name: string }
        | { op: "rename"; id: string; name: string }
        | { op: "delete"; id: string }
        | { op: "order"; ids: string[] }
        | { op: "addItem"; id: string; text: string }
        | ({ op: "updateItem"; id: string; item: string } & Partial<UpdateChecklistItemRequest>)
        | { op: "deleteItem"; id: string; item: string }
        | { op: "orderItems"; id: string; ids: string[] },
    ) => {
      switch (a.op) {
        case "create":
          return post<Checklist[]>(base, { name: a.name });
        case "rename":
          return patch<Checklist[]>(`${base}/${a.id}`, { name: a.name });
        case "delete":
          return del<Checklist[]>(`${base}/${a.id}`);
        case "order":
          return put<Checklist[]>(`${base}/order`, { ids: a.ids });
        case "addItem":
          return post<Checklist[]>(`${base}/${a.id}/items`, { text: a.text });
        case "updateItem":
          return patch<Checklist[]>(`${base}/${a.id}/items/${a.item}`, { text: a.text ?? null, is_checked: a.is_checked ?? null });
        case "deleteItem":
          return del<Checklist[]>(`${base}/${a.id}/items/${a.item}`);
        case "orderItems":
          return put<Checklist[]>(`${base}/${a.id}/items/order`, { ids: a.ids });
      }
    },
    onSuccess: (lists) => {
      qc.setQueryData([...taskKey(projectKey, key), "checklists"], lists);
      void qc.invalidateQueries({ queryKey: [...taskKey(projectKey, key), "detail"] });
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

// ───────────── Custom field values ─────────────

export const useSetFieldValue = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, ({ field, value }: { field: string; value: unknown }) =>
    value === null
      ? del<FieldValue[]>(`${taskPath(projectKey, key)}/fields/${field}`)
      : put<FieldValue[]>(`${taskPath(projectKey, key)}/fields/${field}`, { value }),
  );

// ───────────── Files ─────────────

/** What a photo declares: its pixel size, read in the browser. Other files declare nothing. */
async function declare(file: File): Promise<{ kind: AttachmentKind; width: number | null; height: number | null }> {
  if (!file.type.startsWith("image/") || file.type === "image/svg+xml") return { kind: "file", width: null, height: null };
  try {
    const bmp = await createImageBitmap(file);
    const out = { kind: "photo" as const, width: bmp.width, height: bmp.height };
    bmp.close();
    const ratio = Math.max(out.width, out.height) / Math.max(1, Math.min(out.width, out.height));
    return ratio > 20 ? { kind: "file", width: null, height: null } : out;
  } catch {
    return { kind: "file", width: null, height: null };
  }
}

/** Request → PUT bytes → confirm. `base` is `…/files` or `…/comments/uploads`. */
async function uploadFile(base: string, file: File): Promise<FileView> {
  const d = await declare(file);
  const up = await post<FileUpload>(base, {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
    ...d,
  });
  await uploadToPresignedUrl(up.upload_url, file);
  return post<FileView>(`${base}/${up.file.id}/complete`);
}

export function useUploadTaskFile(projectKey: string, key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile(`${taskPath(projectKey, key)}/files`, file),
    onSuccess: () => invalidateTask(qc, projectKey, key),
  });
}

export const useDeleteTaskFile = (projectKey: string, key: string) =>
  useTaskMutation(projectKey, key, (id: string) => del(`${taskPath(projectKey, key)}/files/${id}`));

// ───────────── Comments ─────────────

export const threadKey = (projectKey: string, key: string) => [...taskKey(projectKey, key), "comments"] as const;

/** The thread, newest page first in `pages`; render `pages` reversed for oldest-first. */
export function useThread(projectKey: string, key: string) {
  return useInfiniteQuery({
    queryKey: threadKey(projectKey, key),
    queryFn: ({ pageParam }) =>
      get<ChatPage>(`${taskPath(projectKey, key)}/comments${pageParam ? `?before=${pageParam}` : ""}`),
    initialPageParam: "",
    getNextPageParam: (last) => (last.has_more ? last.messages[0]?.id : undefined),
  });
}

export function useMarkThreadRead(projectKey: string, key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post(`${taskPath(projectKey, key)}/comments/read`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useThreadMutation(projectKey: string, key: string) {
  const qc = useQueryClient();
  const base = `${taskPath(projectKey, key)}/comments`;
  return useMutation({
    mutationFn: async (
      a:
        | { op: "send"; body: string; files: File[] }
        | { op: "edit"; id: string; body: string }
        | { op: "delete"; id: string }
        | { op: "hide"; id: string },
    ): Promise<ChatMessage | void> => {
      switch (a.op) {
        case "send": {
          const ids: string[] = [];
          for (const f of a.files) ids.push((await uploadFile(`${base}/uploads`, f)).id);
          return post<ChatMessage>(base, { body: a.body, file_ids: ids });
        }
        case "edit":
          return patch<ChatMessage>(`${base}/${a.id}`, { body: a.body });
        case "delete":
          return del<ChatMessage>(`${base}/${a.id}`);
        case "hide":
          return post(`${base}/${a.id}/hide`);
      }
    },
    onSuccess: () => {
      // The task itself too: its comment count is in the header.
      void qc.invalidateQueries({ queryKey: taskKey(projectKey, key) });
      void qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

/** One change to many tasks, all or nothing. `projectKey = null`: tasks from any projects. */
export function useBulkTasks(projectKey: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { keys: string[]; change: BulkChange }) =>
      post<BulkTaskResult>(projectKey ? `${projectPath(projectKey)}/tasks/bulk` : "/tasks/bulk", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tasks"] });
      void qc.invalidateQueries({ queryKey: ["task"] });
      void qc.invalidateQueries({ queryKey: ["statuses"] });
      void qc.invalidateQueries({ queryKey: ["sprints"] });
      void qc.invalidateQueries({ queryKey: ["releases"] });
    },
  });
}

// ── Saved views

const viewsPath = (projectKey: string | null) => (projectKey ? `${projectPath(projectKey)}/views` : "/views");

export function useViews(projectKey: string | null) {
  return useQuery({ queryKey: ["views", projectKey ?? "*"], queryFn: () => get<SavedView[]>(viewsPath(projectKey)) });
}

export function useViewMutations(projectKey: string | null) {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ["views", projectKey ?? "*"] });
  return {
    create: useMutation({ mutationFn: (body: CreateViewRequest) => post<SavedView>(viewsPath(projectKey), body), onSuccess: done }),
    update: useMutation({ mutationFn: ({ id, ...body }: UpdateViewRequest & { id: string }) => patch<SavedView>(`/views/${id}`, body), onSuccess: done }),
    remove: useMutation({ mutationFn: (id: string) => del(`/views/${id}`), onSuccess: done }),
    reorder: useMutation({ mutationFn: (ids: string[]) => post<void>("/views/reorder", { ids }), onSuccess: done }),
  };
}
